import { Injectable, NgZone } from '@angular/core';
import { AuthService } from './auth.service';
import { ref, onValue, push, set, onChildAdded, remove, onDisconnect, off, get, serverTimestamp, update } from 'firebase/database';
import { httpsCallable } from 'firebase/functions';
import { BehaviorSubject, Subscription } from 'rxjs';

import { SessionService } from './session.service';
import { AudioClassifierService, AudioDetectionEvent } from './audio-classifier.service';

export interface RoomConfig {
    frameRate?: number;
    resolution?: string;
    video?: boolean;
    audio?: boolean;
    whiteNoise?: boolean;
    whiteNoiseEaseSeconds?: number;
    color?: string;
    colorEaseSeconds?: number;
    stoppedByOwner?: boolean;
    volume?: number;
    brightness?: number;
    audioDetectionEnabled?: boolean;
    camAudioMuteUntil?: number | null;
    videoMuted?: boolean;
    torchOn?: boolean;
}

export type AudioSubtype = 'cry' | 'whimper' | 'wail' | 'breathing' | 'snoring' | 'cough' | 'speech' | 'babbling';

export interface LogEntry {
    type: 'audio' | 'system';
    subtype: string;
    startedAt: number;
    endedAt: number | null;
    confidence: number;
    avgConfidence?: number;
    source: 'cam' | 'parent';
}

export interface PeerSession {
    pc: RTCPeerConnection;
    dc?: RTCDataChannel;
    iceBuffer: RTCIceCandidateInit[];
    uid: string;
    sessionId?: string | null;
}

@Injectable({
    providedIn: 'root'
})
export class CallsService {
    private peers = new Map<string, PeerSession>();
    private localStream: MediaStream | null = null;
    private roomId: string | null = null;
    private myUid: string | null = null;
    private heartbeatInterval: any = null;

    // UI Observables
    public status$ = new BehaviorSubject<string>('Initializing...');
    public isStreaming$ = new BehaviorSubject<boolean>(false);
    public localStream$ = new BehaviorSubject<MediaStream | null>(null);
    public config$ = new BehaviorSubject<RoomConfig | null>(null);
    public metadata$ = new BehaviorSubject<any>(null);
    public fatalError$ = new BehaviorSubject<string | null>(null);

    // Mesh Presence: List of viewers with connection state
    public viewers$ = new BehaviorSubject<any[]>([]);

    private config: RTCConfiguration = {
        iceServers: [{ urls: 'stun:stun.cloudflare.com:3478' }]
    };
    private turnFetchPromise: Promise<void> | null = null;
    private signalingQueue: Promise<void> = Promise.resolve();

    // P1-4: Network online listener
    private onlineHandler: (() => void) | null = null;

    // Audio detection state
    private audioDetectionEnabled = true;
    private audioClassifierRunning = false;
    private audioClassifierSub: Subscription | null = null;
    private activeLogIds = new Map<string, string>();  // subtype -> log pushId

    // Mute state (driven by room_config)
    private camAudioMuteUntil = 0;
    private videoMuted = false;
    private muteExpiryTimer: any = null;

    // Torch state (driven by room_config)
    private torchOn = false;

    // Battery state — single cache reported via heartbeat + on change.
    // chargingTime/dischargingTime are seconds, normalized to null when Infinity/unknown.
    public battery$ = new BehaviorSubject<{
        level: number | null;
        charging: boolean | null;
        chargingTime: number | null;
        dischargingTime: number | null;
    }>({ level: null, charging: null, chargingTime: null, dischargingTime: null });
    private batteryLevel: number | null = null;
    private batteryCharging: boolean | null = null;
    private batteryChargingTime: number | null = null;
    private batteryDischargingTime: number | null = null;

    constructor(
        private auth: AuthService,
        private sessionService: SessionService,
        private ngZone: NgZone,
        private audioClassifier: AudioClassifierService
    ) { }

    async startLocalStream(facingMode: 'user' | 'environment' = 'environment') {
        if (this.localStream) {
            // If track matches facing mode (approx check), return it.
            // Simplified: if already active, stop and restart if switching?
            // For startLocalStream, we usually assume clean start or idempotent.
            // But here we want to enforce facingMode.
            const vidTrack = this.localStream.getVideoTracks()[0];
            const settings = vidTrack.getSettings();
            if (settings.facingMode === facingMode) {
                this.localStream$.next(this.localStream);
                return this.localStream;
            }

            // If mismatch, stop and get new
            this.stopLocalStream();
        }

        try {
            this.localStream = await navigator.mediaDevices.getUserMedia({
                video: { facingMode },
                audio: true
            });
            this.applyMuteStateToStream();
            if (this.torchOn) this.applyTorchState(true);
            this.localStream$.next(this.localStream);
            this.maybeStartAudioClassifier();
            return this.localStream;
        } catch (streamErr) {
            console.error('Failed to get user media', streamErr);
            throw streamErr;
        }
    }

    async switchCamera(facingMode: 'user' | 'environment') {
        // 1. Get new stream
        const newStream = await navigator.mediaDevices.getUserMedia({
            video: { facingMode },
            audio: true // We also rejoin audio to keep sync, or reuse? easier to get both.
        });

        // 2. Replace Track in Local Stream Object (to keep reference alive if used elsewhere)
        const oldVideoTrack = this.localStream?.getVideoTracks()[0];
        if (oldVideoTrack) oldVideoTrack.stop();

        // We replace the entire localStream ref or just tracks?
        // safest is to update the reference and tracks.
        this.localStream = newStream;
        this.applyMuteStateToStream();
        if (this.torchOn) this.applyTorchState(true);
        this.localStream$.next(newStream);
        const newVideoTrack = newStream.getVideoTracks()[0];

        // 3. Replace Track in All Peer Connections
        this.peers.forEach(peer => {
            const sender = peer.pc.getSenders().find(s => s.track?.kind === 'video');
            if (sender) {
                sender.replaceTrack(newVideoTrack).catch(err => console.error('Track replacement failed', err));
            }
        });
    }

    stopLocalStream() {
        if (this.localStream) {
            this.localStream.getTracks().forEach(t => t.stop());
            this.localStream = null;
            this.localStream$.next(null);
        }
        this.stopAudioClassifier();
    }

    private maybeStartAudioClassifier() {
        if (this.audioClassifierRunning) return;
        if (!this.audioDetectionEnabled) return;
        if (!this.localStream) return;
        const hasAudio = this.localStream.getAudioTracks().length > 0;
        if (!hasAudio) return;

        this.audioClassifierRunning = true;
        this.audioClassifierSub = this.audioClassifier.events$.subscribe(evt => this.onAudioEvent(evt));
        this.audioClassifier.start(this.localStream).catch(err => {
            console.warn('[cam/calls] audio classifier start failed', err);
            this.audioClassifierRunning = false;
        });
    }

    private stopAudioClassifier() {
        if (!this.audioClassifierRunning) return;
        this.audioClassifierRunning = false;
        this.audioClassifierSub?.unsubscribe();
        this.audioClassifierSub = null;
        this.audioClassifier.stop().catch(() => { /* noop */ });
        this.activeLogIds.clear();
    }

    private async onAudioEvent(evt: AudioDetectionEvent) {
        if (!this.roomId) return;
        if (evt.kind === 'start') {
            try {
                const logsRef = ref(this.auth.realtimeDb, `room_events/${this.roomId}`);
                const newRef = push(logsRef);
                this.activeLogIds.set(evt.subtype, newRef.key!);
                await set(newRef, {
                    type: 'audio',
                    subtype: evt.subtype,
                    startedAt: evt.timestamp,
                    endedAt: null,
                    confidence: evt.confidence,
                    source: 'cam'
                });
            } catch (err) {
                console.warn('[cam/calls] log start write failed', err);
                this.activeLogIds.delete(evt.subtype);
            }
        } else {
            const id = this.activeLogIds.get(evt.subtype);
            if (!id) return;
            this.activeLogIds.delete(evt.subtype);
            try {
                await update(ref(this.auth.realtimeDb, `room_events/${this.roomId}/${id}`), {
                    endedAt: evt.timestamp,
                    confidence: evt.confidence,
                    avgConfidence: evt.avgConfidence ?? evt.confidence
                });
            } catch (err) {
                console.warn('[cam/calls] log end update failed', err);
            }
        }
    }

    applyAudioDetectionFlag(enabled: boolean) {
        this.audioDetectionEnabled = enabled;
        if (enabled) {
            this.maybeStartAudioClassifier();
        } else {
            this.stopAudioClassifier();
        }
    }

    private applyMuteStateToStream() {
        if (!this.localStream) return;
        const audioEnabled = !(this.camAudioMuteUntil > Date.now());
        const videoEnabled = !this.videoMuted;
        this.localStream.getAudioTracks().forEach(t => t.enabled = audioEnabled);
        this.localStream.getVideoTracks().forEach(t => t.enabled = videoEnabled);
    }

    private async applyTorchState(desired: boolean) {
        const track = this.localStream?.getVideoTracks()[0];
        if (!track) return;
        const caps = (track.getCapabilities?.() as any) || {};
        if (!caps.torch) {
            if (desired && this.roomId) {
                console.warn('[cam/calls] torch not supported on this device/track');
                try {
                    await update(ref(this.auth.realtimeDb, `room_config/${this.roomId}`), { torchOn: false });
                } catch (e) {
                    console.error('[cam/calls] failed to revert torchOn', e);
                }
            }
            this.torchOn = false;
            return;
        }
        try {
            await track.applyConstraints({ advanced: [{ torch: desired } as any] });
            this.torchOn = desired;
        } catch (e) {
            console.error('[cam/calls] applyConstraints torch failed', e);
            if (desired && this.roomId) {
                try {
                    await update(ref(this.auth.realtimeDb, `room_config/${this.roomId}`), { torchOn: false });
                } catch (revertErr) {
                    console.error('[cam/calls] failed to revert torchOn', revertErr);
                }
            }
            this.torchOn = false;
        }
    }

    private scheduleMuteExpiry() {
        if (this.muteExpiryTimer) {
            clearTimeout(this.muteExpiryTimer);
            this.muteExpiryTimer = null;
        }
        const delay = this.camAudioMuteUntil - Date.now();
        if (delay <= 0) return;
        this.muteExpiryTimer = setTimeout(async () => {
            this.muteExpiryTimer = null;
            this.camAudioMuteUntil = 0;
            this.applyMuteStateToStream();
            if (this.roomId) {
                try {
                    await update(ref(this.auth.realtimeDb, `room_config/${this.roomId}`), { camAudioMuteUntil: null });
                } catch (e) {
                    console.error('[cam/calls] failed to clear camAudioMuteUntil on expiry', e);
                }
            }
        }, delay);
    }

    /* =========================================
       SECURE INVITE JOIN
       ========================================= */

    async joinViaInvite(inviteId: string): Promise<string> {
        this.myUid = this.auth.currentUser!.uid;

        // 0. Ensure Permissions (Microphone/Camera) BEFORE Handshake
        // This ensures the prompt appears immediately after scanning.
        try {
            await this.startLocalStream();
        } catch (permErr) {
            console.error('Permission denied or failed to start stream', permErr);
            throw new Error('Camera/Microphone permissions denied');
        }

        this.status$.next('Handshaking...');

        // 1. Listen to Invite for "Accepted" signal
        // We no longer blindly trust the initial roomId, because Parent might redirect us (Reuse).
        const inviteRef = ref(this.auth.realtimeDb, `cam_invites/${inviteId}`);

        return new Promise((resolve, reject) => {
            let hasCandidateWritten = false;

            const timeout = setTimeout(() => {
                off(inviteRef); // Stop listening
                reject(new Error('Invite timed out'));
            }, 60000);

            onValue(inviteRef, async (snap) => {
                if (!snap.exists()) {
                    // Invite deleted? Likely means we missed the train or it was invalid.
                    // But if we already wrote candidate, maybe parent deleted it?
                    // With new logic, parent deletes after delay. So if it's gone, it's gone.
                    return;
                }

                const val = snap.val();

                // A. Check Basics
                if (!val.roomId) return;

                // A1. Reject expired invites
                if (val.expiresAt && Date.now() > val.expiresAt) {
                    off(inviteRef);
                    clearTimeout(timeout);
                    reject(new Error('Invite has expired'));
                    return;
                }

                // B. Write Candidate ONCE
                if (!hasCandidateWritten) {
                    hasCandidateWritten = true;
                    // Write ourselves as candidate
                    const candidateRef = ref(this.auth.realtimeDb, `cam_invites/${inviteId}/candidate`);
                    await set(candidateRef, this.myUid)
                        .then(() => this.status$.next('Waiting for Host...'))
                        .catch((writeErr) => reject(writeErr));
                }

                // C. Wait for Acceptance
                if (val.status === 'accepted') {
                    off(inviteRef);
                    clearTimeout(timeout);
                    this._finalizeJoin(val.roomId).then(resolve).catch(reject);
                }
            }, (dbErr) => {
                clearTimeout(timeout);
                reject(dbErr);
            });
        });
    }

    private async _finalizeJoin(finalRoomId: string): Promise<string> {
        this.status$.next('Accepted! Joining Room...');

        // D. Double Check Assignment (Safeguard)
        const assignRef = ref(this.auth.realtimeDb, `room_assignments/${finalRoomId}/${this.myUid}`);
        const assignSnap = await get(assignRef);

        if (assignSnap.exists()) {
            // E. Update My User Index
            const role = assignSnap.val().role;
            await set(ref(this.auth.realtimeDb, `users/${this.myUid}/rooms/${finalRoomId}`), { role });

            // Run in Zone to ensure UI updates
            return new Promise<string>((resolve) => {
                this.ngZone.run(() => {
                    this.sessionService.log(finalRoomId, 'CallsService:joinViaInvite', 'Handshake Smart Reuse Complete.');
                    console.log(`[CallsService] Emitting Status. Observers: ${this.status$.observers.length}`);
                    this.status$.next('Handshake Complete.');
                    console.log(`[CallsService] Status Emitted.`);
                    resolve(finalRoomId);
                });
            });
        } else {
            throw new Error('Assignment verification failed');
        }
    }

    // ... (rest of methods)

    private unsubs: (() => void)[] = [];

    // ...

    async updateConfig(updates: Partial<RoomConfig>): Promise<void> {
        if (!this.roomId) return;
        await update(ref(this.auth.realtimeDb, `room_config/${this.roomId}`), updates);
    }

    async endSession() {
        if (this.roomId && this.myUid) {
            try {
                await remove(ref(this.auth.realtimeDb, `room_presence/${this.roomId}/camera/${this.myUid}`));
                await update(ref(this.auth.realtimeDb, `room_metadata/${this.roomId}`), {
                    camEndedManually: true
                });
            } catch (e) {
                console.error('Failed to clean up RTDB on end session', e);
            }
        }
        this.cleanup();
    }

    cleanup() {
        this.unsubs.forEach(fn => fn());
        this.unsubs = [];
        if (this.heartbeatInterval) {
            clearInterval(this.heartbeatInterval);
            this.heartbeatInterval = null;
        }
        if (this.muteExpiryTimer) {
            clearTimeout(this.muteExpiryTimer);
            this.muteExpiryTimer = null;
        }
        this.camAudioMuteUntil = 0;
        this.videoMuted = false;
        this.torchOn = false;
        // P1-4: Remove network listener
        if (this.onlineHandler) {
            window.removeEventListener('online', this.onlineHandler);
            this.onlineHandler = null;
        }
        this.stopSession();
        this.roomId = null;
        this.turnFetchPromise = null;
        this.signalingQueue = Promise.resolve();
    }

    private closePeers() {
        this.peers.forEach(p => p.pc.close());
        this.peers.clear();
        this.updateViewersList({});
    }

    private stopSession() {
        this.stopLocalStream();
        this.closePeers();
    }

    private startHeartbeatLoop() {
        if (this.heartbeatInterval) clearInterval(this.heartbeatInterval);
        this.heartbeatInterval = setInterval(async () => {
            if (this.roomId) {
                // A. Write to Metadata (Keep Alive)
                await update(ref(this.auth.realtimeDb, `room_metadata/${this.roomId}`), {
                    lastCamHeartbeat: serverTimestamp()
                });

                // B. Refresh presence + battery so viewers always have a
                // fresh reading. Uses update so we don't clobber unknown siblings.
                if (this.myUid) {
                    update(ref(this.auth.realtimeDb, `room_presence/${this.roomId}/camera/${this.myUid}`),
                        this.buildPresencePayload()
                    ).catch(err => console.warn('[cam/calls] presence heartbeat failed', err));
                }

                // C. Broadcast to ALL connected peers
                const payload = JSON.stringify({ timestamp: Date.now() });
                this.peers.forEach(peer => {
                    if (peer.dc && peer.dc.readyState === 'open') {
                        peer.dc.send(payload);
                    }
                });
            }
        }, 5000);
    }

    private buildPresencePayload() {
        const hasBattery = this.batteryLevel !== null || this.batteryCharging !== null;
        return {
            online: true,
            heartbeatAt: serverTimestamp(),
            battery: hasBattery
                ? {
                    level: this.batteryLevel,
                    charging: this.batteryCharging,
                    chargingTime: this.batteryChargingTime,
                    dischargingTime: this.batteryDischargingTime
                }
                : null
        };
    }

    reportBattery(
        level: number | null,
        charging: boolean | null,
        chargingTime: number | null = null,
        dischargingTime: number | null = null
    ) {
        this.batteryLevel = level;
        this.batteryCharging = charging;
        this.batteryChargingTime = chargingTime;
        this.batteryDischargingTime = dischargingTime;
        this.battery$.next({ level, charging, chargingTime, dischargingTime });
        if (this.roomId && this.myUid) {
            update(ref(this.auth.realtimeDb, `room_presence/${this.roomId}/camera/${this.myUid}`),
                this.buildPresencePayload()
            ).catch(err => console.warn('[cam/calls] battery write failed', err));
        }
    }

    private lastViewersMap: any = {};

    private updateViewersList(viewersMap: any) {
        this.lastViewersMap = viewersMap;
        const list = Object.keys(viewersMap).map(uid => {
            const peer = this.peers.get(uid);
            const entry = viewersMap[uid] || {};
            const role = entry.role;
            const battery = entry.battery && typeof entry.battery === 'object' ? entry.battery : null;
            const state = peer ? peer.pc.connectionState : 'disconnected';
            return { uid, role, state, battery };
        });
        this.viewers$.next(list);
    }

    async initialize(roomId: string) {
        this.cleanup(); // Safety check
        this.roomId = roomId;
        this.myUid = this.auth.currentUser!.uid;
        this.sessionService.log(roomId, 'CallsService:initialize', 'Initializing CallsService...');
        console.log(`[cam/calls] initialize roomId=${roomId} uid=${this.myUid}`);
        this.ensureTurnCredentials().catch(err => console.error('[cam/calls] prefetch TURN failed', err));

        // 0. Listen to Config & Metadata
        const configRef = ref(this.auth.realtimeDb, `room_config/${roomId}`);
        const unsubConfig = onValue(configRef, (snap) => {
            if (snap.exists()) {
                const cfg = snap.val() as RoomConfig;
                this.config$.next(cfg);
                const detectEnabled = cfg.audioDetectionEnabled !== false;
                if (detectEnabled !== this.audioDetectionEnabled) {
                    this.applyAudioDetectionFlag(detectEnabled);
                }
                this.camAudioMuteUntil = cfg.camAudioMuteUntil || 0;
                this.videoMuted = !!cfg.videoMuted;
                this.applyMuteStateToStream();
                this.scheduleMuteExpiry();
                const desiredTorch = !!cfg.torchOn;
                if (desiredTorch !== this.torchOn) {
                    this.applyTorchState(desiredTorch);
                }
            }
        }, (configErr) => this.handleFatalError('Config Permission Denied', configErr));
        this.unsubs.push(unsubConfig);

        const metaRef = ref(this.auth.realtimeDb, `room_metadata/${roomId}`);
        const unsubMeta = onValue(metaRef, (snap) => {
            if (snap.exists()) {
                this.metadata$.next(snap.val());
            }
        });
        this.unsubs.push(unsubMeta);

        // 1. Register Presence & Assignment
        try {
            const presenceRef = ref(this.auth.realtimeDb, `room_presence/${roomId}/camera/${this.myUid}`);
            await onDisconnect(presenceRef).remove();
            await set(presenceRef, this.buildPresencePayload());

            // Ensure assignment exists
            const assignmentRef = ref(this.auth.realtimeDb, `room_assignments/${roomId}/${this.myUid}`);
            await set(assignmentRef, { role: 'camera', lastActive: Date.now() });

            // Reset Manual End Flag
            await update(ref(this.auth.realtimeDb, `room_metadata/${roomId}`), {
                camEndedManually: false
            });
        } catch (assignErr) {
            this.handleFatalError('Presence/Assignment Failed', assignErr);
            return;
        }

        // 2. Start local preview immediately (independent of viewer presence)
        try {
            await this.startLocalStream();
            this.status$.next('Waiting for viewers...');
        } catch (streamErr) {
            console.error('[cam/calls] initial startLocalStream failed', streamErr);
            this.sessionService.log(roomId, 'CallsService:initialize', `startLocalStream failed: ${(streamErr as Error).message}`);
        }

        // 3. Listen for viewer presence (drives TRANSMITTING state & peer lifecycle)
        const viewersRef = ref(this.auth.realtimeDb, `room_presence/${roomId}/viewers`);
        const unsubViewers = onValue(viewersRef, (snapshot) => {
            const viewersMap = snapshot.val() || {};
            const viewerUids = Object.keys(viewersMap);
            const hasViewers = viewerUids.length > 0;

            this.updateViewersList(viewersMap);

            if (hasViewers) {
                this.status$.next('Streaming to ' + viewerUids.length + ' viewer(s)');
                this.isStreaming$.next(true);
            } else {
                this.isStreaming$.next(false);
                this.status$.next('Waiting for viewers...');
                this.closePeers();
            }
        }, (viewersErr) => this.handleFatalError('Viewers Listen Failed', viewersErr));
        this.unsubs.push(unsubViewers);

        // 3. Mailbox Listener (Signaling)
        const mailboxRef = ref(this.auth.realtimeDb, `room_signaling/${roomId}/${this.myUid}`);

        // Wipe any backlog from previous sessions before attaching the listener.
        // onChildAdded would otherwise replay every queued offer concurrently.
        try {
            await remove(mailboxRef);
        } catch (e) {
            console.warn('[cam/calls] Could not clear mailbox backlog', e);
        }

        const unsubSignaling = onChildAdded(mailboxRef, (snapshot) => {
            const msg = snapshot.val();
            const key = snapshot.key;
            if (!msg || !key) return;
            // Serialize all offer processing — never handle two offers concurrently.
            // The inner function MUST always resolve, otherwise a rejection poisons
            // the chain and silently drops every subsequent message.
            this.signalingQueue = this.signalingQueue.then(async () => {
                try {
                    await this.handleMessage(msg);
                } catch (err) {
                    console.error('[cam/calls] signaling handler failed', err);
                } finally {
                    await remove(ref(this.auth.realtimeDb, `room_signaling/${roomId}/${this.myUid}/${key}`))
                        .catch(delErr => console.error('Signaling cleanup failed', delErr));
                }
            });
        }, (sigListenErr) => this.handleFatalError('Signaling Listen Failed', sigListenErr));
        this.unsubs.push(unsubSignaling);

        // Start Global Heartbeat Loop (once)
        this.startHeartbeatLoop();

        // P1-4: Re-register presence and update TURN config when network restores
        this.onlineHandler = () => {
            console.log('[cam/calls] Network back online');
            if (this.roomId && this.myUid) {
                const presenceRef = ref(this.auth.realtimeDb, `room_presence/${this.roomId}/camera/${this.myUid}`);
                onDisconnect(presenceRef).remove();
                set(presenceRef, this.buildPresencePayload());
            }
            // Update TURN config for any peers in failed/disconnected state
            this.peers.forEach((peer, uid) => {
                const state = peer.pc.connectionState;
                if (state === 'failed' || state === 'disconnected') {
                    this.retryWithTurn(uid, peer.pc);
                }
            });
        };
        window.addEventListener('online', this.onlineHandler);
    }

    private async startSession() {
        await this.startLocalStream();
        // Note: We don't create an offer. We wait for viewers to offer.
    }



    private async handleMessage(msg: any) {
        const senderUid = msg.sender;
        if (!senderUid) return;

        if (msg.type === 'offer') {
            await this.handleOffer(senderUid, msg.data, msg.sessionId || null);
        } else if (msg.type === 'candidate') {
            const peer = this.peers.get(senderUid);
            if (peer) {
                const msgSessionId = msg.sessionId ?? null;
                if (msgSessionId !== peer.sessionId) {
                    console.warn(`[cam/calls] ignoring stale candidate from=${senderUid} session=${msg.sessionId || 'none'}`);
                    return;
                }
                if (peer.pc.remoteDescription && peer.pc.signalingState !== 'closed') {
                    peer.pc.addIceCandidate(new RTCIceCandidate(msg.data)).catch(candErr => console.error(candErr));
                } else {
                    peer.iceBuffer.push(msg.data);
                }
            }
        }
    }



    private async handleOffer(senderUid: string, sdp: string, sessionId: string | null) {
        console.log(`[cam/calls] handleOffer from=${senderUid} session=${sessionId || 'none'}`);
        // Only re-acquire the local stream if it was lost; don't clobber the
        // current facingMode (e.g. 'user') with the default 'environment'.
        if (!this.localStream) {
            try { await this.startLocalStream(); } catch (offerEnsureErr) {
                console.error('[cam/calls] startLocalStream failed in handleOffer', offerEnsureErr);
                return;
            }
        }

        try {
            await this.ensureTurnCredentials();
        } catch (err) {
            console.error('[cam/calls] TURN prefetch failed in handleOffer, continuing with STUN only', err);
        }

        let peer = this.peers.get(senderUid);
        let createdNewPeer = false;
        if (peer) {
            const sessionChanged = sessionId !== peer.sessionId;
            const unusable =
                peer.pc.signalingState === 'closed' ||
                peer.pc.connectionState === 'failed' ||
                peer.pc.connectionState === 'disconnected' ||
                peer.pc.iceConnectionState === 'failed';

            if (sessionChanged || unusable) {
                console.log(`[cam/calls] Replacing peer for ${senderUid}; sessionChanged=${sessionChanged} unusable=${unusable}`);
                peer.pc.close();
                this.peers.delete(senderUid);
                peer = this.createPeerSession(senderUid, sessionId);
                this.peers.set(senderUid, peer);
                createdNewPeer = true;
            }
        }

        if (peer) {
            if (peer.pc.signalingState !== 'stable') {
                // P2-10: Notify offerer of glare instead of silently dropping
                console.warn(`[cam/calls] Glare: peer ${senderUid} not stable, signalingState=${peer.pc.signalingState}`);
                try {
                    await this.sendSignal(senderUid, 'glare', null, sessionId);
                } catch (glareErr) {
                    console.error('[cam/calls] glare sendSignal failed', glareErr);
                }
                return;
            }
            console.log(`[cam/calls] Reusing existing peer for ${senderUid}, updating config for possible ICE restart`);
            peer.pc.setConfiguration(this.config);
        } else {
            peer = this.createPeerSession(senderUid, sessionId);
            this.peers.set(senderUid, peer);
            createdNewPeer = true;
        }

        const pc = peer.pc;
        try {
            await pc.setRemoteDescription(new RTCSessionDescription({ type: 'offer', sdp }));
            console.log(`[cam/calls] Remote description set (offer) for ${senderUid}`);

            // Flush Candidates
            while (peer.iceBuffer.length > 0) {
                const c = peer.iceBuffer.shift();
                pc.addIceCandidate(new RTCIceCandidate(c!)).catch(bufCandErr => console.error(bufCandErr));
            }

            const answer = await pc.createAnswer();
            await pc.setLocalDescription(answer);
            console.log(`[cam/calls] Local description set (answer), sending to ${senderUid}`);
            await this.sendSignal(senderUid, 'answer', answer.sdp, peer.sessionId);
        } catch (negoErr) {
            console.error(`[cam/calls] Offer negotiation failed for ${senderUid}`, negoErr);
            // Drop the half-built peer so the next offer starts clean instead of
            // bouncing off the glare check forever.
            if (createdNewPeer) {
                pc.close();
                this.peers.delete(senderUid);
                this.updateViewersList(this.lastViewersMap);
            }
            throw negoErr;
        }
    }

    private createPeerSession(targetUid: string, sessionId: string | null): PeerSession {
        console.log(`[cam/calls] Creating peer session for ${targetUid} session=${sessionId || 'none'} iceServers=${JSON.stringify((this.config.iceServers || []).map(s => s.urls))}`);
        const pc = new RTCPeerConnection(this.config);

        // Add Tracks
        if (this.localStream) {
            this.localStream.getTracks().forEach(t => pc.addTrack(t, this.localStream!));
        }

        // Data Channel (Passive)
        let dc: RTCDataChannel | undefined;
        pc.ondatachannel = (ev) => {
            console.log('DC from', targetUid);
            dc = ev.channel;
            const peer = this.peers.get(targetUid);
            if (peer) peer.dc = dc;
        };

        // ICE
        pc.onicecandidate = (ev) => {
            if (ev.candidate) {
                console.log(`[cam/calls] local ICE candidate peer=${targetUid} type=${ev.candidate.type} protocol=${ev.candidate.protocol}`);
                this.sendSignal(targetUid, 'candidate', ev.candidate.toJSON(), sessionId);
            } else {
                console.log(`[cam/calls] ICE gathering complete peer=${targetUid}`);
            }
        };

        // P0-1: Track failures per peer; P0-1 cam: remove peer stuck failed for 60s
        let failedTimer: any = null;
        pc.onconnectionstatechange = () => {
            console.log(`[cam/calls] peer=${targetUid} connectionState=${pc.connectionState}`);
            this.updateViewersList(this.lastViewersMap);
            if (pc.connectionState === 'failed') {
                this.retryWithTurn(targetUid, pc);
                // If still failed after 60s with no new offer from viewer, clean up the dead peer
                if (!failedTimer) {
                    failedTimer = setTimeout(() => {
                        failedTimer = null;
                        if (pc.connectionState === 'failed') {
                            console.log(`[cam/calls] Peer ${targetUid} stuck failed for 60s, removing`);
                            pc.close();
                            this.peers.delete(targetUid);
                            this.updateViewersList(this.lastViewersMap);
                        }
                    }, 60000);
                }
            }
            if (pc.connectionState === 'connected') {
                if (failedTimer) { clearTimeout(failedTimer); failedTimer = null; }
            }
        };

        // P0-2: Act on iceConnectionState, not just connectionState
        let iceDisconnectTimer: any = null;
        pc.oniceconnectionstatechange = () => {
            console.log(`[cam/calls] peer=${targetUid} iceConnectionState=${pc.iceConnectionState}`);
            if (pc.iceConnectionState === 'disconnected') {
                iceDisconnectTimer = setTimeout(() => {
                    if (pc.iceConnectionState === 'disconnected') {
                        console.log(`[cam/calls] peer=${targetUid} ICE disconnected 10s, updating TURN config`);
                        this.retryWithTurn(targetUid, pc);
                    }
                    iceDisconnectTimer = null;
                }, 10000);
            }
            if (pc.iceConnectionState === 'failed' && pc.connectionState !== 'failed') {
                if (iceDisconnectTimer) { clearTimeout(iceDisconnectTimer); iceDisconnectTimer = null; }
                this.retryWithTurn(targetUid, pc);
            }
            if (pc.iceConnectionState === 'connected' || pc.iceConnectionState === 'completed') {
                if (iceDisconnectTimer) { clearTimeout(iceDisconnectTimer); iceDisconnectTimer = null; }
            }
        };

        pc.onsignalingstatechange = () => {
            console.log(`[cam/calls] peer=${targetUid} signalingState=${pc.signalingState}`);
        };

        pc.onicegatheringstatechange = () => {
            console.log(`[cam/calls] peer=${targetUid} iceGatheringState=${pc.iceGatheringState}`);
        };

        return { pc, dc, iceBuffer: [], uid: targetUid, sessionId };
    }



    private async sendSignal(targetUid: string, type: string, data: any, sessionId?: string | null) {
        if (!this.roomId || !this.myUid) return;
        const payload = {
            type,
            sender: this.myUid,
            data,
            sessionId: sessionId ?? null,
            timestamp: Date.now()
        };
        // Push to recipient's mailbox
        await push(ref(this.auth.realtimeDb, `room_signaling/${this.roomId}/${targetUid}`), payload);
    }

    private async retryWithTurn(targetUid: string, pc: RTCPeerConnection) {
        if (!this.roomId) return;

        console.log(`[cam/calls] Peer ${targetUid} failed, fetching TURN and updating config; awaiting offerer ICE restart`);
        try {
            await this.ensureTurnCredentials();
            if (this.config.iceServers && this.config.iceServers.length > 0) {
                pc.setConfiguration(this.config);
                console.log(`[cam/calls] Updated peer ${targetUid} config with TURN; waiting for offerer to send iceRestart offer`);
            }
        } catch (turnErr) {
            console.error('[cam/calls] Failed to get TURN creds', turnErr);
        }
    }

    private ensureTurnCredentials(force = false): Promise<void> {
        if (!force && this.turnFetchPromise) return this.turnFetchPromise;
        const p = (async () => {
            if (!this.roomId) return;
            const requestTurnFn = httpsCallable<{ action: string, roomId?: string }, RTCIceServer[]>(this.auth.functions, 'requestTurnCredentials');
            console.log('[cam/calls] Fetching TURN credentials');
            const result = await requestTurnFn({ action: 'getTurnCredentials', roomId: this.roomId });
            if (result.data && (result.data as any).length > 0) {
                this.config.iceServers = [
                    { urls: 'stun:stun.cloudflare.com:3478' },
                    ...(result.data as RTCIceServer[])
                ];
                console.log(`[cam/calls] TURN credentials received, ${(result.data as any).length} servers`);
            } else {
                console.warn('[cam/calls] TURN fetch returned empty');
            }
        })();
        p.catch(() => { if (this.turnFetchPromise === p) this.turnFetchPromise = null; });
        this.turnFetchPromise = p;
        return p;
    }
    private handleFatalError(context: string, fatalErr: any) {
        console.error(`[CallsService] FATAL: ${context}`, fatalErr);
        this.sessionService.log(this.roomId || 'unknown', 'CallsService:fatalError', `FATAL: ${context} - ${fatalErr.message}`);
        this.stopSession();
        this.fatalError$.next(`${context}: ${fatalErr.message}`);
    }
}
